// Deleting a survey, from its page's danger zone or its row on the list
// (consistency-pass spec §4): one confirmation, one rule.

import { toast } from "@pej/kore/components";
import { ApiError, isConstraintViolation } from "@/api/error";
import { useDeleteSurvey } from "@/api/surveys";
import { toastMutationError } from "@/components/error-toast";
import { IrreversibleConfirmDialog } from "@/components/section-page/irreversible-confirm";
import { countOf } from "@/lib/plural";

/** Why the survey can't be deleted, or undefined when it can. Its
 *  placements go with it, so only responses stand in the way. */
export function surveyDeleteRefusal(responseCount: number): string | undefined {
    if (responseCount > 0) return `This survey has ${countOf(responseCount, "response")}. Deactivate it instead.`;
    return undefined;
}

export interface DeleteSurveyDialogProps {
    workspaceId: string;
    survey: { id: string; name: string };
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onDeleted: () => void;
    /** After a refusal without its own response count, the reason in the
     *  caller's freshly refetched data, in surveyDeleteRefusal's words;
     *  without one, "Failed to delete the survey" (never the server's words). */
    refusalAfterConflict: () => string | undefined;
}

export function DeleteSurveyDialog({
    workspaceId,
    survey,
    open,
    onOpenChange,
    onDeleted,
    refusalAfterConflict,
}: DeleteSurveyDialogProps) {
    const deleteSurvey = useDeleteSurvey(workspaceId, survey.id);
    // A response landed since load: the refusal's own count, else the
    // caller's refetched data. A placement racing the delete carries no
    // count and none shows there: the fallback, and a retry deletes it.
    const refusal = (err: ApiError) => {
        const responseCount = err.countParam("responseCount");
        return responseCount !== undefined ? surveyDeleteRefusal(responseCount) : refusalAfterConflict();
    };
    return (
        <IrreversibleConfirmDialog
            open={open}
            onOpenChange={onOpenChange}
            verb="Delete"
            entityName="survey"
            matchValue={survey.name}
            matchLabel="survey name"
            consequences="Its steps and placements go with it. Their links, QR codes and embeds stop working."
            isPending={deleteSurvey.isPending}
            onConfirm={async () => {
                try {
                    await deleteSurvey.mutateAsync();
                    toast.success("Survey deleted");
                    onDeleted();
                } catch (err) {
                    onOpenChange(false);
                    if (err instanceof ApiError) {
                        if (isConstraintViolation(err)) toast.error(refusal(err) ?? "Failed to delete the survey");
                        else toastMutationError("Delete failed", err);
                        return;
                    }
                    throw err;
                }
            }}
        />
    );
}
